import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { can } from "../lib/permissions";
import { api, coverUri } from "../lib/api";
import { Spinner, EmptyState, Chip, SongRow, Button } from "../components/ui";
import { Icon } from "../components/icons";

const FILTERS = [
  { key: "", label: "TODAS" },
  { key: "por_estudar", label: "POR ESTUDAR" },
  { key: "em_estudo", label: "EM ESTUDO" },
  { key: "preparada", label: "PREPARADAS" },
];

export default function Repertorio() {
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [picking, setPicking] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSetlist = can(user, "setlists.edit");
  const togglePick = (id) => setPicking((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  useEffect(() => {
    const t = setTimeout(() => setQuery(text), 250);
    return () => clearTimeout(t);
  }, [text]);

  const q = useQuery({
    queryKey: ["songs", query, status],
    queryFn: () => api(`/songs?q=${encodeURIComponent(query)}&status=${status}`),
  });

  const songs = Array.isArray(q.data) ? q.data : q.data?.songs || [];

  return (
    <>
      <div className="page-header" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <h1 className="t-h1" style={{ flex: 1 }}>Repertório</h1>
          {canSetlist ? (
            picking ? (
              <>
                <Button className="auto sm ghost" onClick={() => setPicking(null)} data-testid="repertoire-pick-cancel">CANCELAR</Button>
                <Button className="auto sm" icon="list" disabled={!picking.length} onClick={() => navigate(`/admin/setlists?new=1&songs=${picking.join(",")}`)} data-testid="repertoire-pick-create">CRIAR ALINHAMENTO ({picking.length})</Button>
              </>
            ) : <Button className="auto sm ghost" icon="add" onClick={() => setPicking([])} data-testid="repertoire-new-setlist">NOVO ALINHAMENTO</Button>
          ) : null}
        </div>
        {picking ? <div className="t-hint" data-testid="repertoire-pick-hint">Toque nas músicas pela ordem pretendida. Poderá reordenar a seguir.</div> : null}
        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 48, borderRadius: 999, background: "var(--surface-2)", border: "1px solid var(--border)", padding: "0 16px", maxWidth: 520 }}>
          <Icon name="search" size={18} color="var(--muted)" />
          <input
            data-testid="repertoire-search-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Pesquisar música…"
            style={{ flex: 1, background: "none", border: "none", outline: "none", color: "var(--on-surface)", fontSize: 16, fontFamily: "var(--f-regular)" }}
          />
        </div>
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
          {FILTERS.map((f) => (
            <Chip key={f.key} label={f.label} active={status === f.key} onClick={() => setStatus(f.key)} data-testid={`filter-${f.key || "todas"}`} />
          ))}
        </div>
      </div>

      {q.isLoading ? (
        <Spinner />
      ) : q.isError ? (
        <div className="page" style={{ maxWidth: 1200 }}>
          <EmptyState icon="cloud-offline-outline" text="Não foi possível carregar o repertório." />
          <Button className="auto" onClick={() => q.refetch()} data-testid="repertoire-retry-button">TENTAR NOVAMENTE</Button>
        </div>
      ) : (
        <div className="page ulc-fade" style={{ maxWidth: 1200 }} data-testid="repertoire-list">
          {songs.length ? (
            <>
              <div style={{ color: "var(--muted)", fontFamily: "var(--f-medium)", fontSize: 11, letterSpacing: 2, marginBottom: 12 }}>
                {songs.length} {songs.length === 1 ? "MÚSICA" : "MÚSICAS"}
              </div>
              <div className="grid-cards">
                {songs.map((s) => (picking ? (
                  <button key={s.id} className="card" onClick={() => togglePick(s.id)} aria-pressed={picking.includes(s.id)} data-testid={`repertoire-pick-${s.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, cursor: "pointer", color: "inherit", textAlign: "left", ...(picking.includes(s.id) ? { borderColor: "var(--brand-primary)", background: "var(--brand-tertiary)" } : {}) }}>
                    <span className="track-num" style={{ color: "var(--brand-primary)" }}>{picking.includes(s.id) ? picking.indexOf(s.id) + 1 : ""}</span>
                    <img className="cover" src={coverUri(s.cover_url)} alt="" style={{ width: 44, height: 44, borderRadius: 6 }} />
                    <span style={{ flex: 1, minWidth: 0 }}><div style={{ fontFamily: "var(--f-medium)" }}>{s.title}</div><div className="t-hint">{s.artist}</div></span>
                    <Icon name={picking.includes(s.id) ? "checkmark" : "add"} size={18} color={picking.includes(s.id) ? "var(--brand-primary)" : "var(--muted)"} />
                  </button>
                ) : (
                  <SongRow key={s.id} song={s} testid={`song-row-${s.title.toLowerCase().replace(/\s+/g, "-")}`} />
                )))}
              </div>
            </>
          ) : (
            <EmptyState icon="search-outline" text="Nenhuma música encontrada." testid="repertoire-empty" />
          )}
        </div>
      )}
    </>
  );
}
